import { Check, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardFooter, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { PointKindSelect } from '@/components/ui/PointKindSelect'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { cn } from '@/lib/cn'
import type { LegPointsPayload, LegRoutePoint, RoutePointKind } from '@/types/domain'

import { reachedCount } from './routeChain'

/* Typing a leg's route chain by hand (issue #35).
 *
 * Until this existed, the chain could only arrive from a direction template:
 * `PUT /cases/:id/legs/:legId/points` was built, tested and mounted, and no
 * control in the product submitted it except the overdue strip's one-point
 * advance. A corridor that ran slightly differently this month could not be
 * corrected at all — the desk's only move was to re-apply a template, which
 * overwrites the arrivals somebody had been recording.
 *
 * ── Two facts this form has to keep visible ──────────────────────────────────
 *
 * ARRIVALS ARE PINNED TO POSITIONS, not to names. `Repo.ReplacePoints` reads the
 * stored chain keyed by position and re-applies each instant to whatever now
 * sits at that position, so moving a point up carries the arrival of the row it
 * displaced, not its own. That is the right rule for the ordinary edit (fixing a
 * spelling, adding a stop at the end) and a surprise for a reorder, so the
 * notice says it in words and the reached rows are marked with a tick where
 * their number would be.
 *
 * THE ENDS FOLLOW THE CHAIN. `replacePoints` calls `SetLegEnds` in the same
 * transaction: the first point becomes the leg's origin and the last its
 * destination, which the case list's route summary and the invoice's shipment
 * block both render. Renaming the first row renames the leg. The subtitle says
 * so, because nothing else on this card would.
 *
 * ── Reordering is buttons, not a drag handle ─────────────────────────────────
 *
 * A drag handle was drawn and rejected. HTML5 drag-and-drop does not fire on
 * touch at all, and this desk works from phones at border posts; a handle that
 * is inert on the device the work happens on is worse than a control that looks
 * plainer. Two chevrons move a row, work with a thumb and a keyboard alike, and
 * are reachable by a screen reader with the row number in their name.
 *
 * ── What it does NOT ask ─────────────────────────────────────────────────────
 *
 * Whether a point is reached. That is the timeline's control next door, and one
 * of the two surfaces has to own it: a checkbox here would let a submission set
 * a prefix while the reader was looking at a different one. This form submits
 * the arrivals EXACTLY as they stand.
 */

/** One row as typed.
 *
 *  `days` is a STRING for `DirectionFormSheet`'s reason: `Number('')` is 0, and
 *  a numeric state would turn a half-cleared field into a saved zero — here that
 *  means a transition the position advances through the instant it begins. */
interface PointDraft {
  /** React's key, and nothing else — never submitted. Stored points key by their
   *  server id, which is stable across every move and removal this form can
   *  perform; new rows get a minted one. Keying by array index reconciles the
   *  state INSIDE a row — an open country list, a text selection — onto
   *  different data the moment a row is removed or moved. */
  key: string
  name: string
  /** ISO 3166-1 alpha-2, or `''`. */
  country: string
  kind: RoutePointKind
  /** Days from the previous point, as typed. Never read on the first row. */
  days: string
}

/** The server's ceiling (`maxLegPoints`), repeated so the refusal arrives beside
 *  the control rather than as a 422 after a round trip. */
const MAX_POINTS = 20

/* The server's floor is deliberately NOT two, which is why no constant states
   one: ZERO is legal — a leg nobody has broken into hops, which is what every
   leg is until a corridor is applied — and ONE is refused, because a single stop
   has no transition in it and cannot synchronise the leg's ends. `tooFew` below
   tests that exact length rather than a `< 2` comparison. See
   `LegPointsChange.Validate`. */

/** The name bound (`maxShortTextLength`), shared with the leg's own endpoints so
 *  a point and an end cannot mean different lengths. */
const MAX_NAME = 200

let pointSeq = 0

function emptyPoint(): PointDraft {
  pointSeq += 1
  return { key: `new-${pointSeq}`, name: '', country: '', kind: 'waypoint', days: '' }
}

/** Why a row's day count is refused, as a translation KEY rather than a
 *  sentence — `DirectionFormSheet` makes the same split for the same reason:
 *  threading `t` through a helper either widens its signature to
 *  `(key: string) => string`, throwing the compile-time catalogue check away, or
 *  drags a type in that nobody else here needs.
 *
 *  A second copy of the settings sheet's rule, deliberately: the two forms write
 *  to two different tables with two different validators, and the SERVER is the
 *  authority either way. Importing one feature's helper into the other would
 *  couple them into agreeing about a rule they are not obliged to share. */
type DayProblem = 'common.form.required' | 'settings.directions.errors.wholeDays'

function dayProblem(raw: string): DayProblem | undefined {
  const trimmed = raw.trim()
  // Required on every point but the first: that is the transition the
  // auto-advance is measured over, and a missing number is not a duration.
  if (trimmed === '') return 'common.form.required'
  // Whole days. `parseInt` would silently accept "3.7" as 3.
  if (!/^\d+$/.test(trimmed)) return 'settings.directions.errors.wholeDays'
  return undefined
}

export function RouteEditor({
  legId,
  points,
  onSubmit,
  onCancel,
  pending,
  error,
}: {
  /** Seeds the draft, and re-seeds it if the reader navigates between legs
   *  without the card unmounting. */
  legId: string
  points: LegRoutePoint[]
  onSubmit: (payload: LegPointsPayload) => void
  onCancel: () => void
  pending?: boolean
  /** The server's refusal, rendered under the rows that produced it. */
  error?: string
}) {
  const { t } = useTranslation()

  /* How many leading positions carry an arrival TODAY. Submitted unchanged —
     this form moves route planning, never the record of where the cargo has
     been. */
  const marked = reachedCount(points)

  const [draft, setDraft, touched, markTouched] = useFormDraft<PointDraft[]>(legId, () =>
    points.length === 0
      ? [emptyPoint(), emptyPoint()]
      : points.map((point) => ({
          key: point.id,
          name: point.name,
          country: point.country ?? '',
          kind: point.kind ?? 'waypoint',
          // The first point's absent duration is not a cleared field, it is the
          // absence of an inbound transition; it never reaches an input.
          days:
            point.plannedDaysFromPrevious === undefined
              ? ''
              : String(point.plannedDaysFromPrevious),
        })),
  )

  const nameProblem = (row: PointDraft) =>
    !row.name.trim()
      ? t('common.form.required')
      : row.name.trim().length > MAX_NAME
        ? t('common.form.tooLong')
        : undefined
  /* Row 0 is exempt in BOTH directions: it must not carry a duration, so its
     control never accepts one and its value is never read. */
  const rowDayProblem = (row: PointDraft, index: number) =>
    index === 0 ? undefined : dayProblem(row.days)

  const tooFew = draft.length === 1
  const tooMany = draft.length > MAX_POINTS
  const invalid =
    tooFew ||
    tooMany ||
    draft.some(
      (row, index) =>
        nameProblem(row) !== undefined || rowDayProblem(row, index) !== undefined,
    )

  const setRow = (index: number, next: Partial<PointDraft>) =>
    setDraft(draft.map((row, i) => (i === index ? { ...row, ...next } : row)))

  const move = (index: number, by: -1 | 1) => {
    const to = index + by
    /* Both rows are READ before either is written. The destructuring swap that
       used to stand here reads them as possibly-absent and writes them back
       as such, which is the one way this function could put a hole in the
       chain — and a hole in the chain is a point the server never hears
       about. */
    const from = draft[index]
    const onto = draft[to]
    if (!from || !onto) return
    const next = [...draft]
    next[index] = onto
    next[to] = from
    setDraft(next)
  }

  const submit = () => {
    markTouched()
    if (invalid || pending) return
    onSubmit({
      points: draft.map((row, index) => ({
        name: row.name.trim(),
        // Blank is "nobody stated this", not an empty country — the wire drops
        // it rather than sending `""`, matching every other optional here.
        country: row.country.trim() === '' ? undefined : row.country.trim(),
        kind: row.kind,
        // Absent on the first point and only there; the server enforces the
        // correspondence both ways.
        plannedDaysFromPrevious: index === 0 ? undefined : Number(row.days.trim()),
        // Untouched. The prefix that stands is the prefix that is submitted.
        reached: index < marked,
      })),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cases.legPage.routeEditTitle')}</CardTitle>
        <CardSubtitle>{t('cases.legPage.routeEditSubtitle')}</CardSubtitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-3">
        {/* A NOTICE rather than an error: nothing has gone wrong, and this is a
            standing statement about what saving does. Red on a warning nobody
            has triggered yet is how a product teaches people to dismiss red. */}
        {marked > 0 ? (
          <p className="bg-surface-sunken text-text-muted rounded-md px-3 py-2 text-[12.5px] leading-snug">
            {t('cases.legPage.routeEditArrivalsPinned')}
          </p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {error}
          </p>
        ) : touched && invalid ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {tooFew
              ? t('cases.legPage.routeEditTooFew')
              : tooMany
                ? t('cases.legPage.routeEditTooMany', { max: MAX_POINTS })
                : t('common.form.fixFields')}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          {draft.map((row, index) => {
            const nameError = touched ? nameProblem(row) : undefined
            const dayKey = touched ? rowDayProblem(row, index) : undefined
            const held = index < marked

            return (
              <div
                key={row.key}
                className="grid grid-cols-[20px_22px_auto_minmax(0,1fr)_150px_96px_28px] items-start gap-1.5"
              >
                {/* Reordering. Stacked so the pair occupies one control's height
                    and the row keeps the rhythm of the sheet it came from. */}
                <div className="flex flex-col">
                  <button
                    type="button"
                    disabled={index === 0}
                    aria-label={t('cases.legPage.movePointUp', { n: index + 1 })}
                    onClick={() => move(index, -1)}
                    className="text-text-faint hover:text-text-body flex size-[18px] cursor-pointer items-center justify-center rounded-[4px] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronUp size={13} aria-hidden />
                  </button>
                  <button
                    type="button"
                    disabled={index === draft.length - 1}
                    aria-label={t('cases.legPage.movePointDown', { n: index + 1 })}
                    onClick={() => move(index, 1)}
                    className="text-text-faint hover:text-text-body flex size-[18px] cursor-pointer items-center justify-center rounded-[4px] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronDown size={13} aria-hidden />
                  </button>
                </div>

                {/* The position, which is the one fact about a row that is not
                    typed anywhere on it — and, where an arrival is pinned to that
                    position, a tick instead of the number. */}
                <span
                  className={cn(
                    'mt-1.5 flex size-[22px] shrink-0 items-center justify-center rounded-full border text-[11px] font-medium',
                    held
                      ? 'border-positive bg-positive text-white'
                      : 'bg-surface-sunken border-line text-text-muted',
                  )}
                  title={held ? t('cases.legPage.arrivalHeldHere') : undefined}
                >
                  {held ? <Check size={12} strokeWidth={2.5} aria-hidden /> : index + 1}
                </span>

                {/* The flag ALONE and it LEADS the row, exactly as the leg sheet's
                    endpoints and the direction sheet's rows do: a country narrows
                    a place, so it is answered first, and a second full-width
                    control repeating "Turkmenistan" would compete with "Ashgabat"
                    for a line that is really one fact. */}
                <CountryDropdown
                  flagOnly
                  value={row.country}
                  onChange={(country) => setRow(index, { country })}
                  aria-label={t('settings.directions.pointCountry', { n: index + 1 })}
                  className="w-auto shrink-0 px-2.5"
                />

                <div className="flex min-w-0 flex-col gap-1">
                  <Input
                    value={row.name}
                    aria-label={t('settings.directions.pointName', { n: index + 1 })}
                    placeholder={t('settings.directions.pointPlaceholder')}
                    aria-invalid={nameError !== undefined || undefined}
                    onChange={(e) => setRow(index, { name: e.target.value })}
                  />
                  {nameError ? (
                    <span className="text-negative text-[11.5px] leading-snug">
                      {nameError}
                    </span>
                  ) : null}
                </div>

                <PointKindSelect
                  value={row.kind}
                  onChange={(kind) => setRow(index, { kind })}
                  ariaLabel={t('routePoints.kindN', { n: index + 1 })}
                />

                {/* The transition INTO this point. The first row has none — it is
                    where the cargo starts — so its control is disabled and reads
                    «старт» rather than being hidden: an absent field on row one
                    and a present one on row two would look like a rendering bug,
                    while a disabled one says the fact does not exist here. */}
                <div className="flex shrink-0 flex-col gap-1">
                  <Input
                    // `inputMode` rather than type="number", whose spinners and
                    // locale-dependent decimals this project avoids throughout.
                    inputMode="numeric"
                    disabled={index === 0}
                    value={
                      index === 0 ? t('settings.directions.firstPointStart') : row.days
                    }
                    aria-label={t('settings.directions.pointDays', { n: index + 1 })}
                    placeholder={t('settings.directions.daysPlaceholder')}
                    aria-invalid={dayKey !== undefined || undefined}
                    onChange={(e) => setRow(index, { days: e.target.value })}
                  />
                  {dayKey ? (
                    <span className="text-negative text-[11.5px] leading-snug">
                      {t(dayKey)}
                    </span>
                  ) : null}
                </div>

                <IconButton
                  icon={Trash2}
                  size="sm"
                  className="mt-0.5"
                  label={t('settings.directions.removePoint', { n: index + 1 })}
                  onClick={() => setDraft(draft.filter((_, i) => i !== index))}
                />
              </div>
            )
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {draft.length >= MAX_POINTS ? (
            <span className="text-text-muted text-[12px] leading-snug font-light">
              {t('cases.legPage.routeEditTooMany', { max: MAX_POINTS })}
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              iconLeft={Plus}
              onClick={() => setDraft([...draft, emptyPoint()])}
            >
              {t('settings.directions.addPoint')}
            </Button>
          )}
          <span className="text-text-muted asa-num text-[12px] leading-snug font-light">
            {t('settings.directions.pointCount', { count: draft.length })}
          </span>
        </div>
      </CardBody>

      <CardFooter>
        <Button size="sm" onClick={submit} disabled={pending}>
          {pending ? t('common.form.saving') : t('cases.legPage.routeEditSave')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={pending}>
          {t('common.actions.cancel')}
        </Button>
        {/* Clearing the chain entirely is legal and is NOT the same act as
            removing rows one by one until the form refuses at one point. Offered
            only when there is something to clear, and only past the floor, so it
            never reads as the way to empty a two-row form. */}
        {points.length > 0 ? (
          <span className="text-text-muted ml-auto text-[12px] leading-snug font-light">
            {t('cases.legPage.routeEditEndsHint')}
          </span>
        ) : null}
      </CardFooter>
    </Card>
  )
}
